import _contactFormFields from '@fixtures/contact-form-fields.json'
import { usePiwikPro } from '@piwikpro/next-piwik-pro'
import { useEffect, useState } from 'react'

export const DATA_LAYER_KEY_NAMES = {
  formContacts: 'formContatto',
}

export const DATA_LAYER_EVENT_NAMES = {
  formContactsDataLayerEventTrigger: 'contacts_dl_event',
}

const FORM_CONTACTS = {
  nome: null,
  cognome: null,
  email: null,
  telefono: null,
  polizzaAdesione: null,
  nomeProdotto: null,
  agenziaDiRiferimento: null,
  codiceFiscale: null,
  indirizzo: null,
  allegato: null,
  richiesta: null,
  infoContatto: null,
  cosaVuoiSapere: null,
  tema: null,
  tipoEvento: null,
  numeroSinistro: null,
}

const useCustomDataLayerTracking = ({
  keyName = DATA_LAYER_KEY_NAMES.formContacts,
  triggerEventName = DATA_LAYER_EVENT_NAMES.formContactsDataLayerEventTrigger,
  options: {
    prefixFormFields: _prefixFormFields = {},
    formFields: _formFields = {},
  },
}) => {
  const [dataLayer, setDataLayer] = useState(null)
  const [visitorId, setVisitorId] = useState(null)
  const { UserManagement, DataLayer, CustomEvent } = usePiwikPro()
  const [startTime, setStartTime] = useState(null)
  const [depthLevel, setDepthLevel] = useState(0)

  const getFormFields = (keyName) => {
    switch (keyName) {
      case DATA_LAYER_KEY_NAMES.formContacts:
        return {
          prefixFormFields: _prefixFormFields,
          formFields: _contactFormFields,
        }
      default:
        return { prefixFormFields: _prefixFormFields, formFields: _formFields }
    }
  }

  const anonimizeDataLayer = ({ formContatto: _formContatto }) => {
    const formKeysToAnonimize = [
      'richiesta',
      'agenziaDiRiferimento',
      'codiceFiscale',
      'cognome',
      'email',
      'indirizzo',
      'nome',
      'telefono',
      'polizzaAdesione',
      'numeroSinistro',
    ]
    const formContatto = { ..._formContatto }

    const hash = (value) => value.replace(/./g, '*')

    for (const key in formContatto) {
      if (formKeysToAnonimize.includes(key)) {
        formContatto[key] = hash(formContatto[key])
      }
    }
    return { formContatto }
  }

  const handleDataLayerChange = (
    _data,
    startTimeMeasurement = false,
    endTimeMeasurement = true,
    _keyName = keyName
  ) => {
    const currentTime = new Date()
    const formData = getFormFields(_keyName)
    const { prefixFormFields, formFields } = formData
    const data = mapDataLayerValue(_data, _keyName, formData)
    const { depthLevel: _depthLevel = 0 } = data
    if (_depthLevel > 0) {
      setDepthLevel(_depthLevel)
    }
    const prefixFormKeys = extractLabels(prefixFormFields, _keyName).slice(
      0,
      depthLevel
    )

    const dataToPush = getDataToPush(
      data,
      startTimeMeasurement,
      endTimeMeasurement,
      currentTime
    )

    const completionPercentage = getCompletionPercentage(
      dataToPush[keyName],
      false,
      prefixFormKeys
    )
    const completionPercentageRequired = getCompletionPercentage(
      dataToPush[keyName],
      true,
      prefixFormKeys
    )
    dataToPush[keyName] = {
      ...dataToPush[keyName],
      completamentoTotale: completionPercentage,
      completamentoObbligatori: completionPercentageRequired,
    }

    const anonimizedDataToPush = anonimizeDataLayer(dataToPush)

    const stringedArray = getStringedArray(anonimizedDataToPush[keyName])
    anonimizedDataToPush[keyName].valori = stringedArray

    setDataLayer(anonimizedDataToPush)
    if (startTimeMeasurement) setStartTime(currentTime)
    DataLayer.push(anonimizedDataToPush)
  }

  const getDataToPush = (
    data,
    startTimeMeasurement,
    endTimeMeasurement,
    currentTime
  ) => {
    // DataLayer is defined, must reset the data because it's a new form entry
    if (startTimeMeasurement && dataLayer && dataLayer[keyName] && visitorId) {
      return {
        [keyName]: {
          ...data,
          ...(endTimeMeasurement
            ? { tempo: getTimeSpent(startTimeMeasurement) }
            : {}),
        },
      }
    }

    if (!dataLayer || !dataLayer[keyName]) {
      return {
        [keyName]: { ...data },
      }
    } else {
      return {
        [keyName]: {
          ...dataLayer[keyName],
          ...data,
          ...(endTimeMeasurement
            ? { tempo: getTimeSpent(startTimeMeasurement) }
            : {}),
        },
      }
    }
  }

  const getStringedArray = (data, ignoreKey = 'valori') => {
    return Object.entries(data)
      .filter(([key]) => key !== ignoreKey)
      .map(([key, value]) => `[ ${key} = ${value} ]`)
      .join(' | ')
  }

  function getFieldNames(objectData) {
    const fieldNames = new Set()
    objectData.fieldGroups.forEach((group) => {
      group.fields.forEach((field) => {
        fieldNames.add(field.name)
      })
    })
    return fieldNames
  }

  function getRequiredFieldNames(objectData) {
    const requiredFieldNames = new Set()
    objectData.fieldGroups.forEach((group) => {
      group.fields.forEach((field) => {
        if (field.required) {
          requiredFieldNames.add(field.name)
        }
      })
    })
    return requiredFieldNames
  }
  const TOTAL_TRACKED_FIELDS_PER_FORM = {
    [DATA_LAYER_KEY_NAMES.formContacts]: {
      total: getFieldNames(_contactFormFields).size,
      required: getRequiredFieldNames(_contactFormFields).size,
    },
  }

  const getCompletionPercentage = (
    data,
    onlyRequired = false,
    additionalRequiredFields = []
  ) => {
    const totalTrackableFields = onlyRequired
      ? TOTAL_TRACKED_FIELDS_PER_FORM[keyName].required +
        additionalRequiredFields.length
      : TOTAL_TRACKED_FIELDS_PER_FORM[keyName].total +
        additionalRequiredFields.length

    const trackedTags = Object.keys(data).reduce((acc, key) => {
      const requiredFields = [
        ...Array.from(getRequiredFieldNames(_contactFormFields)).map(
          (fieldName) => formFieldNamesToDataLayer[fieldName]
        ),
        ...additionalRequiredFields,
      ]
      const excludedKeys = [
        'valori',
        'tempo',
        'completamentoTotale',
        'completamentoObbligatori',
        'privacyPolicyID',
        'visitorId',
        'depthLevel',
      ]
      if (excludedKeys.includes(key)) return acc
      if (onlyRequired) {
        if (requiredFields.includes(key) && data[key]) {
          return acc + 1
        }
      } else if (data[key]) {
        return acc + 1
      }
      return acc
    }, 0)

    return ((trackedTags / totalTrackableFields) * 100).toFixed(1)
  }

  const removeNullishProps = (obj) => {
    const newObj = {}
    for (const key in obj) {
      if (obj[key] === null || obj[key] === undefined || obj[key] === '') {
        continue
      }
      if (typeof obj[key] === 'object') {
        newObj[key] = removeNullishProps(obj[key])
        if (Object.keys(newObj[key]).length === 0) {
          delete newObj[key]
        }
      } else {
        newObj[key] = obj[key]
      }
    }
    return newObj
  }

  const triggerDataLayerEvent = (
    data = null,
    eventType = null,
    event = triggerEventName
  ) => {
    if (
      (eventType === 'leavePage' || eventType === 'formSubmit') &&
      dataLayer &&
      dataLayer[keyName]
    ) {
      const _data = {
        [keyName]: {
          ...dataLayer[keyName],
          tipoEvento: eventType,
        },
      }
      DataLayer.push({
        event,
        ...removeNullishProps(_data),
      })
    } else if (data) {
      DataLayer.push({
        event,
        ...removeNullishProps(data),
      })
    } else {
      DataLayer.push({
        event,
        ...dataLayer,
      })
    }
  }

  const getAsyncVisitorId = async () => {
    const visitorId = await UserManagement.getVisitorId()
    handleDataLayerChange({
      visitorId,
    })
    setVisitorId(visitorId)
  }

  const handleBeforeUnload = (e) => {
    e.preventDefault()
    e.returnValue = ''
    const eventType = 'leavePage'
    triggerDataLayerEvent(dataLayer, eventType)
  }

  const getTimeSpent = (startTime) =>
    Math.floor((Date.now() - startTime) / 1000) % 60

  useEffect(() => {
    if (!visitorId) {
      getAsyncVisitorId()
    }

    if (window) {
      window.addEventListener('beforeunload', handleBeforeUnload)
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [])

  return {
    dataLayer,
    visitorId,
    handleDataLayerChange,
    triggerDataLayerEvent,
  }
}

const formFieldNamesToDataLayer = {
  message: 'richiesta',
  address: 'indirizzo',
  document: 'allegato',
  email: 'email',
  firstName: 'nome',
  lastName: 'cognome',
  policyNumber: 'polizzaAdesione',
  productName: 'nomeProdotto',
  referenceAgency: 'agenziaDiRiferimento',
  taxIdCode: 'codiceFiscale',
  telephone: 'telefono',
  accidentNumber: 'numeroSinistro',
}

function mapContactFormValuesToDataLayer(
  formValues,
  { prefixFormFields, formFields }
) {
  const translateValue = {
    ...formFieldNamesToDataLayer,
  }

  const deletableValues = {
    privacyPolicy: true,
    recipient: true,
  }

  const mappedValue = {
    pathSlices: true,
  }

  const mapPathSlices = (slices) => {
    const extractedPrefixFormKeys = extractLabels(
      prefixFormFields,
      DATA_LAYER_KEY_NAMES.formContacts
    )

    const prefixFormData = slices.reduce((acc, slice, index) => {
      acc[extractedPrefixFormKeys[index]] = slice.trim()
      return acc
    }, {})
    prefixFormData.depthLevel = slices.length

    return prefixFormData
  }

  const mappedData = {}

  for (const key in formValues) {
    if (deletableValues[key]) continue

    if (translateValue[key]) {
      mappedData[translateValue[key]] = formValues[key]
    } else if (mappedValue[key]) {
      switch (key) {
        case 'pathSlices':
          const result = mapPathSlices(formValues[key])
          Object.assign(mappedData, result)
          break
        default:
          mappedData[key] = formValues[key]
      }
    } else {
      mappedData[key] = formValues[key]
    }
  }

  return mappedData
}

function extractLabels(data, keyName) {
  const result = new Set()

  const extractPrefixFormContacts = (_data, _label = 'info contatto') => {
    let label = _label

    if (_label === DATA_LAYER_KEY_NAMES.formContacts) {
      label = 'info contatto'
    }

    for (let item of _data) {
      const { depthLevel, isLastLevel } = item

      if (!result.has(label)) {
        const formattedLabel = formatPrefixFormKeyLabel(label)
        result.add(formattedLabel)
      }

      if (item.nextLevel) {
        extractPrefixFormContacts(
          item.nextLevel.options,
          item.nextLevel.label.text
        )
      }

      if (isLastLevel) {
        continue
      }
    }
  }

  if (keyName === DATA_LAYER_KEY_NAMES.formContacts) {
    extractPrefixFormContacts(data)
  }

  return Array.from(result)
}

function formatPrefixFormKeyLabel(label) {
  return label
    .replace(/[^a-zA-Z0-9]/g, ' ')
    .split(' ')
    .map((word, index) =>
      index === 0
        ? word.toLowerCase()
        : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    )
    .join('')
}
function mapDataLayerValue(data, keyName, formData) {
  switch (keyName) {
    case DATA_LAYER_KEY_NAMES.formContacts:
      return mapContactFormValuesToDataLayer(data, formData)
    default:
      return data
  }
}

export default useCustomDataLayerTracking
